// A workshop door: the list of doors this phone may work, and what to do with a badge
// held up at one.
//
// Unlike lead capture, this phone asks a question rather than recording a fact, so the
// answer has to come from the server. Nothing about who may enter what is ever stored
// here: a door phone holds no roster, for the same reason the lead phone holds no
// attendee details.
//
// What the phone did is stored: every badge read here, and every decision made about
// one, goes into the same archive a lead scan does, so the panel can report on it.

export interface DoorSession {
  id: number
  title: string
  starts_at: string | null
  ends_at: string | null
  room: string | null
  capacity: number | null
  occupancy: number
}

export type EntryVerdict =
  | 'registered'
  | 'waitlisted'
  | 'not_registered'
  | 'unknown_ticket'
  | 'unverified'

export interface EntryResult {
  verdict: EntryVerdict
  admitted: boolean
  already_entered_at: string | null
  // When this badge was last sent away from this door. Always sent, alongside
  // already_entered_at rather than instead of it; it is the door screen that prefers
  // having gone in when the two disagree, so a refusal is only ever shown for somebody
  // still outside.
  last_refused_at: string | null
  occupancy: number
  capacity: number | null
  // Set when the phone could not reach us at all. The person at the door still has to
  // decide, so this is a state to show rather than an error to swallow.
  unreachable?: boolean
}

/**
 * A badge checked at a door, and the archive record it was written to. The door screen
 * hands the uuid back to admit() or refuse(), which keeps one badge one row.
 *
 * Null when the archive refused the write. The check still happens.
 */
export interface DoorCheck {
  result: EntryResult
  scanUuid: string | null
}

// Sent and settled are both finished with; only unreachable stops a drain.
export type DecisionUpload = 'sent' | 'settled' | 'unreachable'

// Every call here is bounded, because none of them was before and a fetch with no
// timeout does not fail: it waits. Venue WiFi that accepts a connection and then
// stalls is the normal bad case, and without a ceiling the door simply stops
// answering, with the phone showing no reason why.
//
// The numbers are the length of time somebody is willing to stand still. A scan has
// to answer inside three seconds or hand the decision to the person holding the
// phone, which is what the unreachable path already does properly.
const SCAN_TIMEOUT_MS = 3000

// Longer, because a decision is a write we want to land rather than a question. Per
// attempt, and decide() makes DECISION_ATTEMPTS of them, so an admission keeps somebody
// waiting at most about ten seconds before saying plainly that nothing was recorded. A
// refusal is not waited on at all, so the same ceiling costs the door nothing.
const DECISION_TIMEOUT_MS = 5000

// Two, and counted by decide() rather than handed to ofetch, whose retry cannot be
// bounded. Ofetch builds the abort signal on the first attempt and retries with the same
// options, so the second attempt inherits the first one's signal with its timer already
// cleared: after a timeout that signal is aborted and the retry fails instantly, and
// after a 5xx it can never fire, so the retry runs with no ceiling at all. A door write
// that hangs for ever after one bad gateway is the exact thing these numbers exist to
// prevent, so every call here passes retry: 0 and decide() does its own.
const DECISION_ATTEMPTS = 2

// Long enough for a moment of bad signal to pass, short enough to stay inside the time
// somebody will stand at a door.
const DECISION_RETRY_DELAY_MS = 250

// A decision sent later, from Upload now. Twice the door's ceiling because nobody is
// standing there waiting on it.
const DECISION_UPLOAD_TIMEOUT_MS = 10000

// The picker, on opening. Four seconds because giving up costs nothing here: the phone
// falls back to the sittings it was last told about, with their headcounts dropped, and
// the alternative is a shift that cannot start because the list never arrives.
const SESSIONS_TIMEOUT_MS = 4000

// Which failures decide() tries again after: ofetch's own retry list, kept exactly, so
// dropping its retry does not quietly change which answers count as final. Everything
// else - any other 4xx, and the 5xx codes that are a definite answer rather than a bad
// moment - is settled, and a second identical request cannot change it.
const RETRYABLE_STATUSES = new Set([408, 409, 425, 429, 500, 502, 503, 504])

export const useWorkshopDoor = () => {
  const config = useRuntimeConfig()
  const device = useScanDevice()
  // Seeded from what the phone last knew, for the same reason as the area list: a door
  // phone that opens with no connection still has to be pointable at a sitting.
  const sessions = useState<DoorSession[]>('door-sessions', () => device.value.knownSessions)

  const authHeaders = () => ({ Authorization: `Bearer ${device.value.token}` })

  // The counts the panel reads, refreshed after every write here.
  const { refresh } = useScanQueue()

  /**
   * Writes the badge down before anything is asked about it, like a lead scan: at a door
   * the server may never answer. Null when the archive refused the write.
   */
  const recordCheck = async (code: string): Promise<DoorScan | null> => {
    try {
      // Not refreshed here. The counts move again the moment the check settles, a
      // second later, and the panel cannot be open while a badge is being scanned.
      return await saveDoorScan(
        device.value.pairingId,
        code,
        device.value.sessionId,
        device.value.sessionTitle,
      )
    } catch {
      return null
    }
  }

  /**
   * Settles the record once we know whether the server answered. A check that never
   * reached us is unrecorded rather than waiting: replaying it hours later would admit
   * somebody who may have walked away.
   */
  const settleCheck = async (scan: DoorScan | null, result: EntryResult | null) => {
    if (!scan) {
      return
    }

    await patchDoorScan(scan.uuid, {
      verdict: result?.verdict ?? null,
      admitted: result?.admitted ?? false,
      verified: result !== null,
      status: result ? 'synced' : 'unrecorded',
      lastError: result ? null : 'no connection',
    }, { attempted: true })

    await refresh()
  }

  /**
   * The doors this phone was assigned, for the picker.
   *
   * Fetched fresh whenever we can reach the server, and remembered so a phone opened with
   * no connection can still be put on a door. What it cannot do offline is answer for
   * anybody — see scan() below — which is what the door screen says plainly.
   */
  const loadSessions = async (): Promise<'ok' | 'deactivated' | 'unreachable'> => {
    if (!device.value.token) {
      return 'deactivated'
    }

    try {
      const response = await $fetch<{ sessions: DoorSession[] }>(
        `${config.public.apiBase}/scan/v1/workshops`,
        // retry: 0 overrides ofetch's default of one retry on a GET. See DECISION_ATTEMPTS.
        { headers: authHeaders(), timeout: SESSIONS_TIMEOUT_MS, retry: 0 },
      )

      sessions.value = response.sessions
      device.value.knownSessions = response.sessions

      return 'ok'
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status

      if (status === 403) {
        return 'deactivated'
      }

      // The counts in a remembered list are whatever they were when we last had a
      // connection, and a stale headcount presented as the current one is worse than
      // none, so they are dropped. The picker never showed them; the door screen fills
      // them in from a scan, which offline it cannot do either way.
      sessions.value = device.value.knownSessions.map(session => ({
        ...session,
        occupancy: 0,
        capacity: null,
      }))

      return 'unreachable'
    }
  }

  /**
   * A badge held up at the door.
   *
   * A confirmed seat comes back already admitted, so the queue moves on one tap.
   * Anything else comes back unadmitted and waits for the person on the door, which is
   * what admit() and refuse() are for.
   *
   * The scan is in the archive before the question is asked, and sent under that
   * record's identifier, so the phone's history and our row describe the same scan.
   */
  const scan = async (code: string): Promise<DoorCheck> => {
    const { eventId, sessionId } = device.value
    const record = await recordCheck(code)

    // A phone that could not write to its storage still has somebody at the door.
    const scanUuid = record?.uuid ?? crypto.randomUUID()
    const scannedAt = record?.scannedAt ?? new Date().toISOString()

    try {
      const result = await $fetch<EntryResult>(
        `${config.public.apiBase}/scan/v1/events/${eventId}/workshops/${sessionId}/scan`,
        {
          method: 'POST',
          headers: authHeaders(),
          timeout: SCAN_TIMEOUT_MS,
          // Ofetch does not retry a POST by default; said out loud so it stays that way.
          retry: 0,
          body: {
            code,
            scan_uuid: scanUuid,
            scanned_at: scannedAt,
          },
        },
      )

      await settleCheck(record, result)

      return { result, scanUuid: record?.uuid ?? null }
    } catch {
      // Deliberately not treated as a refusal. Saying "not registered" because a phone
      // lost signal would turn a connection problem into someone being sent away.
      const result: EntryResult = {
        verdict: 'unverified',
        admitted: false,
        already_entered_at: null,
        last_refused_at: null,
        occupancy: 0,
        capacity: null,
        unreachable: true,
      }

      await settleCheck(record, null)

      return { result, scanUuid: record?.uuid ?? null }
    }
  }

  /**
   * A decision the person on the door made, sent to be recorded.
   *
   * Letting somebody in and sending them away are the same write with opposite meanings,
   * and everything that makes one land safely makes the other land safely too, so they
   * share this rather than keeping two copies of the reasoning below.
   *
   * `verified` is false when the scan itself never reached us, so the entry says nobody
   * checked rather than claiming a verdict.
   */
  const decide = async (
    decision: 'admit' | 'refuse',
    code: string,
    verified: boolean,
    scanUuid: string | null,
  ): Promise<EntryResult | null> => {
    const { eventId, sessionId } = device.value

    // Built once, outside the attempts, which is what makes retrying safe: scan_uuid is
    // generated here and resent unchanged, so a second attempt after a lost response
    // collapses onto the same row through the unique index instead of recording the same
    // decision twice. Its own identifier rather than the check's, which may already
    // hold a row that the unique index would collapse this onto.
    const body = {
      code,
      scan_uuid: crypto.randomUUID(),
      scanned_at: new Date().toISOString(),
      verified,
    }

    // Written to the badge's own record, and set to waiting before the first attempt.
    // A decision that never lands is then already in the backlog.
    if (scanUuid) {
      await patchDoorScan(scanUuid, {
        action: decision,
        // What the door decided, recorded now rather than waiting on the answer, so a
        // decision still queued describes the person correctly.
        admitted: decision === 'admit',
        decisionUuid: body.scan_uuid,
        decidedAt: body.scanned_at,
        verified,
        status: 'pending',
        lastError: null,
      })

      await refresh()
    }

    let lastError = 'no connection'

    // The attempts are counted here rather than handed to ofetch - see DECISION_ATTEMPTS -
    // so each one gets its own five seconds and the worst case is a number rather than
    // however long a stalled socket lasts. The wait comes before an attempt rather than
    // after a failure, so the loop condition is the only thing deciding how many there
    // are, and nobody stands at the door through a delay that leads to no further try.
    for (let attempt = 0; attempt < DECISION_ATTEMPTS; attempt++) {
      if (attempt > 0) {
        await new Promise(resolve => setTimeout(resolve, DECISION_RETRY_DELAY_MS))
      }

      try {
        const written = await $fetch<EntryResult>(
          `${config.public.apiBase}/scan/v1/events/${eventId}/workshops/${sessionId}/${decision}`,
          {
            method: 'POST',
            headers: authHeaders(),
            timeout: DECISION_TIMEOUT_MS,
            retry: 0,
            body,
          },
        )

        if (scanUuid) {
          // The verdict comes from the answer, so the phone's history says what we
          // recorded rather than what the check found.
          await patchDoorScan(scanUuid, {
            verdict: written.verdict,
            admitted: written.admitted,
            status: 'synced',
            lastError: null,
          }, { attempted: true })

          await refresh()
        }

        return written
      } catch (error: unknown) {
        const status = (error as { response?: { status?: number } })?.response?.status

        lastError = status ? `http ${status}` : 'no connection'

        // An answer we were given and that will not change - this badge refused, this
        // phone deactivated - ends it here, so the door hears "not recorded" at once
        // rather than a quarter of a second later. No answer at all is always worth
        // another go: that is the stalled connection this whole file is about.
        if (status && !RETRYABLE_STATUSES.has(status)) {
          break
        }
      }
    }

    // Nothing reached us. The record stays waiting with the reason against it, for
    // Upload now to send later.
    if (scanUuid) {
      await noteAttempt(scanUuid, lastError)
      await refresh()
    }

    return null
  }

  /**
   * Recording someone the person on the door chose to let in anyway: a seat going spare
   * and being filled by whoever turned up, which is the case the log exists for.
   */
  const admit = (
    code: string,
    verified: boolean,
    scanUuid: string | null,
  ): Promise<EntryResult | null> => decide('admit', code, verified, scanUuid)

  /**
   * Recording someone sent away.
   *
   * Written down for the same reason an admission is: so the badge presented again five
   * minutes later shows the decision it already got instead of a fresh verdict, and so a
   * refusal somebody disputes afterwards is answerable.
   */
  const refuse = (
    code: string,
    verified: boolean,
    scanUuid: string | null,
  ): Promise<EntryResult | null> => decide('refuse', code, verified, scanUuid)

  /**
   * A decision that never reached us at the time, sent from the maintenance panel.
   *
   * The door has no automatic retry on purpose; this is for the end of the shift. The
   * entry lands at the time it was made, under the identifier the first attempt used, so
   * a decision that did in fact arrive collapses onto the same row.
   */
  const uploadDecision = async (scan: DoorScan): Promise<DecisionUpload> => {
    const { eventId } = device.value

    // Nothing to send: a check nobody decided on, or a phone that has lost its door.
    // Settled, rather than left in a backlog Upload now can never clear.
    if (!eventId || !scan.sessionId || !scan.decisionUuid || scan.action === 'check') {
      await markScan(scan.uuid, 'unrecorded', 'no decision to send')

      return 'settled'
    }

    try {
      const written = await $fetch<EntryResult>(
        `${config.public.apiBase}/scan/v1/events/${eventId}/workshops/${scan.sessionId}/${scan.action}`,
        {
          method: 'POST',
          headers: authHeaders(),
          timeout: DECISION_UPLOAD_TIMEOUT_MS,
          retry: 0,
          body: {
            code: scan.code,
            scan_uuid: scan.decisionUuid,
            scanned_at: scan.decidedAt,
            verified: scan.verified,
          },
        },
      )

      await patchDoorScan(scan.uuid, {
        verdict: written.verdict,
        admitted: written.admitted,
        status: 'synced',
        lastError: null,
      }, { attempted: true })

      return 'sent'
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status

      // Anything the server answered is final, the same rule the lead path follows.
      if (status && status >= 400 && status < 500 && status !== 429) {
        await markScan(scan.uuid, 'rejected', `http ${status}`)

        return 'settled'
      }

      await noteAttempt(scan.uuid, status ? `http ${status}` : 'no connection')

      return 'unreachable'
    }
  }

  return { sessions, loadSessions, scan, admit, refuse, uploadDecision }
}
